<template>
  <div class="admin-layout">
    <!-- Overlay pour fermer la sidebar sur mobile -->
    <div class="sidebar-overlay" v-if="isMobileOpen" @click="toggleMobileSidebar"></div>
    
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 
      collapsed: isCollapsed && !isMobileOpen, 
      'mobile-open': isMobileOpen 
    }">
      <!-- Logo -->
      <div class="sidebar-header">
        <div class="logo-container">
          <img src="/logo192.png" alt="OKOLISY" class="logo">
          <span v-if="!isCollapsed || isMobileOpen" class="logo-text">OKOLISY Admin</span>
        </div>
        <!-- ✅ La flèche n'apparaît que sur desktop -->
        <button v-if="!isMobileOpen" class="toggle-btn" @click="toggleSidebar">
          <font-awesome-icon :icon="isCollapsed ? 'chevron-right' : 'chevron-left'" />
        </button>
        <!-- ✅ Bouton de fermeture sur mobile -->
        <button v-else class="toggle-btn close-btn" @click="toggleMobileSidebar">
          <font-awesome-icon icon="times" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <router-link to="/admin" class="nav-link" exact @click="closeMobileOnNavigate">
          <font-awesome-icon icon="home" />
          <span v-if="!isCollapsed || isMobileOpen">Tableau de bord</span>
        </router-link>

        <router-link to="/admin/annonces" class="nav-link" @click="closeMobileOnNavigate">
          <font-awesome-icon icon="box" />
          <span v-if="!isCollapsed || isMobileOpen">Annonces</span>
          <span v-if="(!isCollapsed || isMobileOpen) && stats.annonces > 0" class="badge-nav">{{ stats.annonces }}</span>
        </router-link>

        <router-link to="/admin/utilisateurs" class="nav-link" @click="closeMobileOnNavigate">
          <font-awesome-icon icon="users" />
          <span v-if="!isCollapsed || isMobileOpen">Utilisateurs</span>
          <span v-if="(!isCollapsed || isMobileOpen) && stats.users > 0" class="badge-nav">{{ stats.users }}</span>
        </router-link>

        <router-link to="/admin/transactions" class="nav-link" @click="closeMobileOnNavigate">
          <font-awesome-icon icon="credit-card" />
          <span v-if="!isCollapsed || isMobileOpen">Transactions</span>
          <span v-if="(!isCollapsed || isMobileOpen) && stats.transactions > 0" class="badge-nav">{{ stats.transactions }}</span>
        </router-link>

        <router-link to="/admin/paiements" class="nav-link" @click="closeMobileOnNavigate">
          <font-awesome-icon icon="money-bill-wave" />
          <span v-if="!isCollapsed || isMobileOpen">Paiements vendeurs</span>
        </router-link>

        <!-- Section Super Admin -->
        <template v-if="isSuperAdmin">
          <div class="nav-divider" v-if="!isCollapsed || isMobileOpen">Administration</div>
          
          <router-link to="/admin/admins" class="nav-link" @click="closeMobileOnNavigate">
            <font-awesome-icon icon="user-shield" />
            <span v-if="!isCollapsed || isMobileOpen">Gestion admins</span>
          </router-link>

          <router-link to="/admin/settings" class="nav-link" @click="closeMobileOnNavigate">
            <font-awesome-icon icon="cog" />
            <span v-if="!isCollapsed || isMobileOpen">Paramètres</span>
          </router-link>
        </template>
      </nav>

      <!-- Footer -->
      <div class="sidebar-footer">
        <div class="user-info" v-if="!isCollapsed || isMobileOpen">
          <img :src="userAvatar" alt="Admin" class="user-avatar">
          <div>
            <p class="user-name">{{ adminUser?.pseudo || 'Admin' }}</p>
            <p class="user-role">{{ isSuperAdmin ? 'Super Admin' : 'Admin' }}</p>
          </div>
        </div>
        <button class="logout-btn" @click="logout">
          <font-awesome-icon icon="sign-out-alt" />
          <span v-if="!isCollapsed || isMobileOpen">Déconnexion</span>
        </button>
      </div>
    </aside>

    <!-- Main content -->
    <div class="main-area" :class="{ 'mobile-shifted': isMobileOpen }">
      <!-- Header -->
      <header class="admin-header">
        <div class="header-left">
          <button class="mobile-toggle" @click="toggleMobileSidebar">
            <font-awesome-icon icon="bars" />
            <span class="sr-only">Menu</span>
          </button>
          <h1>{{ pageTitle }}</h1>
        </div>
        <div class="header-right">
          <div class="header-search">
            <input 
              type="text" 
              placeholder="Rechercher..." 
              v-model="searchQuery" 
              @keyup.enter="globalSearch"
            >
            <font-awesome-icon icon="search" class="search-icon" />
          </div>
          <div class="header-notifications" @click="toggleNotifications">
            <font-awesome-icon icon="bell" />
            <span class="notif-badge" v-if="notifications.length > 0">{{ notifications.length }}</span>
          </div>
          <div class="header-user" @click="toggleUserMenu">
            <img :src="userAvatar" alt="Admin">
            <span>{{ adminUser?.pseudo || 'Admin' }}</span>
            <font-awesome-icon icon="chevron-down" class="chevron" />
          </div>
        </div>
      </header>

      <!-- Content -->
      <main class="admin-content">
        <router-view />
      </main>
    </div>

    <!-- Notifications dropdown -->
    <div v-if="showNotifications" class="notifications-dropdown" @click="showNotifications = false">
      <div class="notifications-list" @click.stop>
        <div class="notif-header">
          <h3>Notifications</h3>
          <button class="mark-read" @click="markAllRead">Tout marquer comme lu</button>
        </div>
        <div v-if="notifications.length === 0" class="notif-empty">
          <p>Aucune notification</p>
        </div>
        <div v-else class="notif-items">
          <div v-for="notif in notifications" :key="notif.id" class="notif-item" :class="{ unread: !notif.read }">
            <div class="notif-icon" :style="{ background: notif.color }">
              <font-awesome-icon :icon="notif.icon" />
            </div>
            <div class="notif-content">
              <p>{{ notif.message }}</p>
              <span class="notif-time">{{ formatTime(notif.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAdminAuthStore } from '@/Auth/stores/adminAuthStore';
import { $api } from '@/main';

export default defineComponent({
  name: 'AdminLayout',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const adminAuth = useAdminAuthStore();
    
    // États
    const isCollapsed = ref(false);
    const isMobileOpen = ref(false);
    const showNotifications = ref(false);
    const searchQuery = ref('');
    const notifications = ref<any[]>([]);
    const stats = ref({ annonces: 0, users: 0, transactions: 0 });
    const isMobile = ref(window.innerWidth <= 768);

    // Computed
    const adminUser = computed(() => adminAuth.state.user);
    const isSuperAdmin = computed(() => adminUser.value?.role === 'super_admin');

    const pageTitle = computed(() => {
      const titles: Record<string, string> = {
        '/admin': 'Tableau de bord',
        '/admin/annonces': 'Gestion des annonces',
        '/admin/utilisateurs': 'Gestion des utilisateurs',
        '/admin/transactions': 'Transactions',
        '/admin/admins': 'Gestion des administrateurs',
        '/admin/settings': 'Paramètres'
      };
      return titles[route.path] || 'Administration';
    });

    const userAvatar = computed(() => {
      return `https://ui-avatars.com/api/?name=${adminUser.value?.pseudo || 'Admin'}&background=FFA754&color=fff&size=40`;
    });

    // ✅ Gestion du redimensionnement
    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
      if (!isMobile.value) {
        isMobileOpen.value = false;
      }
    };

    // ✅ Toggle sidebar (desktop)
    const toggleSidebar = () => {
      if (isMobile.value) {
        // Sur mobile, on ouvre/ferme complètement
        isMobileOpen.value = !isMobileOpen.value;
      } else {
        // Sur desktop, on collapse/expand
        isCollapsed.value = !isCollapsed.value;
      }
    };

    // ✅ Toggle mobile sidebar
    const toggleMobileSidebar = () => {
      isMobileOpen.value = !isMobileOpen.value;
      // Si on ferme, on s'assure que le collapsed est false pour que la sidebar garde sa largeur
      if (!isMobileOpen.value) {
        isCollapsed.value = false;
      }
    };

    // ✅ Fermer la sidebar mobile après navigation
    const closeMobileOnNavigate = () => {
      if (isMobile.value) {
        isMobileOpen.value = false;
      }
    };

    const toggleNotifications = () => {
      showNotifications.value = !showNotifications.value;
    };

    const toggleUserMenu = () => {
      // À implémenter
    };

    const globalSearch = () => {
      if (searchQuery.value.trim()) {
        router.push(`/admin?search=${searchQuery.value}`);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await $api.get('/api/admin/stats');
        if (response.data.success) {
          const s = response.data.stats;
          stats.value = {
            annonces: s.annonces?.total || 0,
            users: s.users?.total || 0,
            transactions: s.transactions?.total || 0
          };
        }
      } catch (error) {
        console.error('Erreur chargement stats:', error);
      }
    };

    const fetchNotifications = async () => {
      try {
        const response = await $api.get('/api/admin/notifications');
        if (response.data.success) {
          notifications.value = response.data.notifications;
        }
      } catch (error) {
        console.error('Erreur chargement notifications:', error);
      }
    };

    const markAllRead = async () => {
      try {
        await $api.post('/api/admin/notifications/mark-read');
        notifications.value = notifications.value.map(n => ({ ...n, read: true }));
      } catch (error) {
        console.error('Erreur marquage notifications:', error);
      }
    };

    const formatTime = (date: string) => {
      if (!date) return '';
      const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
      if (diff < 60) return 'À l\'instant';
      if (diff < 3600) return `${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      return `${Math.floor(diff / 86400)}j`;
    };

    const logout = async () => {
      await adminAuth.logout();
    };

    onMounted(() => {
      fetchStats();
      fetchNotifications();
      window.addEventListener('resize', handleResize);
      setInterval(fetchStats, 300000);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      isCollapsed,
      isMobileOpen,
      isMobile,
      showNotifications,
      searchQuery,
      notifications,
      stats,
      adminUser,
      isSuperAdmin,
      pageTitle,
      userAvatar,
      toggleSidebar,
      toggleMobileSidebar,
      closeMobileOnNavigate,
      toggleNotifications,
      toggleUserMenu,
      globalSearch,
      markAllRead,
      formatTime,
      logout
    };
  }
});
</script>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.admin-layout {
  display: flex;
  min-height: 100vh;
  background: #f4f7fa;
  width: 100%;
  overflow-x: hidden;
}

// Sidebar overlay
.sidebar-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
}

// Sidebar
.sidebar {
  width: 260px;
  background: linear-gradient(180deg, $orange-fonce 0%, darken($orange-fonce, 15%) 100%);
  color: white;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  height: 100vh;
  z-index: 100;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &.collapsed {
    width: 72px;
    
    .logo-text,
    .nav-link span,
    .badge-nav,
    .user-info,
    .logout-btn span,
    .nav-divider {
      display: none;
    }
    
    .nav-link {
      justify-content: center;
      padding: 12px;
    }
    
    .sidebar-header {
      padding: 16px 12px;
      justify-content: center;
      
      .toggle-btn {
        display: flex;
      }
    }
    
    .sidebar-footer {
      .logout-btn {
        justify-content: center;
        padding: 12px;
        
        span {
          display: none;
        }
      }
    }
  }
}

.sidebar-header {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 76px;

  .logo-container {
    display: flex;
    align-items: center;
    gap: 12px;
    overflow: hidden;

    .logo {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      object-fit: contain;
      flex-shrink: 0;
    }

    .logo-text {
      font-size: 1.1rem;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .toggle-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    &.close-btn {
      background: rgba(255, 0, 0, 0.3);
      
      &:hover {
        background: rgba(255, 0, 0, 0.5);
      }
    }
  }
}

.sidebar-nav {
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
  overflow-x: hidden;

  .nav-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    color: rgba(255, 255, 255, 0.75);
    text-decoration: none;
    border-radius: 8px;
    transition: all 0.2s;
    margin-bottom: 4px;
    position: relative;
    white-space: nowrap;

    svg {
      width: 18px;
      flex-shrink: 0;
    }

    span {
      font-size: 0.9rem;
    }

    .badge-nav {
      margin-left: auto;
      background: rgba(255, 255, 255, 0.2);
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.7rem;
      flex-shrink: 0;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    &.router-link-exact-active,
    &.router-link-active {
      background: rgba(255, 255, 255, 0.15);
      color: white;
    }
  }

  .nav-divider {
    padding: 12px 14px 8px;
    font-size: 0.65rem;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
    letter-spacing: 1px;
  }
}

.sidebar-footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    overflow: hidden;

    .user-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    div {
      overflow: hidden;
      
      .user-name {
        font-size: 0.85rem;
        font-weight: 500;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-role {
        font-size: 0.7rem;
        opacity: 0.7;
        margin: 0;
      }
    }
  }

  .logout-btn {
    width: 100%;
    padding: 10px;
    background: rgba(0, 0, 0, 0.2);
    border: none;
    color: white;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
}

// Main area
.main-area {
  flex: 1;
  margin-left: 260px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: calc(100% - 260px);
  max-width: 100%;
}

// Admin header
.admin-header {
  background: white;
  padding: 14px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 50;
  flex-wrap: wrap;
  gap: 12px;

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;

    .mobile-toggle {
      display: none;
      align-items: center;
      justify-content: center;
      background: $orange-doux;
      color: white;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 1.1rem;
      flex-shrink: 0;
      
      &:hover {
        background: $orange-fonce;
      }
    }

    h1 {
      font-size: 1.2rem;
      color: $gris-fonce;
      margin: 0;
      white-space: nowrap;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .header-search {
      position: relative;

      input {
        padding: 8px 35px 8px 14px;
        border: 1px solid #e9ecef;
        border-radius: 20px;
        font-size: 0.85rem;
        width: 180px;
        transition: all 0.2s;

        &:focus {
          outline: none;
          border-color: $orange-doux;
          width: 220px;
        }
      }

      .search-icon {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: $gris-moyen;
        font-size: 0.8rem;
      }
    }

    .header-notifications {
      position: relative;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      transition: background 0.2s;

      &:hover {
        background: #f5f5f5;
      }

      svg {
        font-size: 1.1rem;
        color: $gris-moyen;
      }

      .notif-badge {
        position: absolute;
        top: 0;
        right: 0;
        width: 18px;
        height: 18px;
        background: #dc3545;
        color: white;
        border-radius: 50%;
        font-size: 0.6rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .header-user {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 12px 4px 4px;
      border-radius: 30px;
      transition: background 0.2s;

      &:hover {
        background: #f5f5f5;
      }

      img {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        object-fit: cover;
      }

      span {
        font-size: 0.85rem;
        font-weight: 500;
        color: $gris-fonce;
      }

      .chevron {
        font-size: 0.6rem;
        color: $gris-moyen;
      }
    }
  }
}

// Admin content
.admin-content {
  padding: 24px;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

// Notifications dropdown
.notifications-dropdown {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 200;
  background: rgba(0, 0, 0, 0.3);
}

.notifications-list {
  position: absolute;
  top: 70px;
  right: 24px;
  width: 360px;
  max-height: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-width: calc(100vw - 48px);

  .notif-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;

    h3 {
      font-size: 0.95rem;
      margin: 0;
    }

    .mark-read {
      background: none;
      border: none;
      color: $orange-doux;
      font-size: 0.75rem;
      cursor: pointer;
    }
  }

  .notif-empty {
    padding: 30px 20px;
    text-align: center;
    color: $gris-moyen;
  }

  .notif-items {
    max-height: 320px;
    overflow-y: auto;
  }

  .notif-item {
    display: flex;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 1px solid #f5f5f5;
    transition: background 0.2s;

    &:hover {
      background: #f8f9fa;
    }

    &.unread {
      background: #fff8f0;
    }

    .notif-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      flex-shrink: 0;
    }

    .notif-content {
      flex: 1;
      min-width: 0;

      p {
        margin: 0 0 4px;
        font-size: 0.85rem;
        color: $gris-fonce;
        word-wrap: break-word;
      }

      .notif-time {
        font-size: 0.65rem;
        color: $gris-moyen;
      }
    }
  }
}

// Responsive
@media (max-width: 1024px) {
  .sidebar {
    width: 72px;

    .logo-text,
    .nav-link span,
    .badge-nav,
    .user-info,
    .logout-btn span,
    .nav-divider {
      display: none;
    }
    
    .nav-link {
      justify-content: center;
      padding: 12px;
    }
    
    .sidebar-header {
      padding: 16px 12px;
      justify-content: center;
      
      .toggle-btn {
        display: none;
      }
    }
    
    .sidebar-footer .logout-btn {
      justify-content: center;
      padding: 12px;
      
      span {
        display: none;
      }
    }
  }

  .main-area {
    margin-left: 72px;
    width: calc(100% - 72px);
  }
}

@media (max-width: 768px) {
  .sidebar-overlay {
    display: block;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  .sidebar {
    transform: translateX(-100%);
    width: 280px !important;
    transition: transform 0.3s ease;
    
    &.mobile-open {
      transform: translateX(0);
      
      .logo-text,
      .nav-link span,
      .badge-nav,
      .user-info,
      .logout-btn span,
      .nav-divider {
        display: inline;
      }
      
      .nav-link {
        justify-content: flex-start;
        padding: 10px 14px;
      }
      
      .sidebar-header {
        padding: 18px 20px;
        justify-content: space-between;
        
        .toggle-btn {
          display: flex;
        }
      }
      
      .sidebar-footer .logout-btn {
        justify-content: center;
        padding: 10px;
        
        span {
          display: inline;
        }
      }
    }
  }

  .sidebar.mobile-open + .sidebar-overlay {
    opacity: 1;
    pointer-events: all;
  }

  .main-area {
    margin-left: 0 !important;
    width: 100% !important;
    
    &.mobile-shifted {
      margin-left: 280px !important;
      width: calc(100% - 280px) !important;
    }
  }

  .admin-header {
    padding: 12px 16px;

    .header-left {
      .mobile-toggle {
        display: flex !important;
      }
      
      h1 {
        font-size: 1rem;
      }
    }

    .header-right {
      .header-search input {
        width: 100px;
        font-size: 0.8rem;

        &:focus {
          width: 140px;
        }
      }
      
      .header-user span {
        display: none;
      }
    }
  }

  .admin-content {
    padding: 12px;
  }

  .notifications-list {
    right: 16px;
    width: calc(100% - 32px);
    top: 60px;
  }
}

@media (max-width: 480px) {
  .admin-header {
    .header-right {
      .header-search {
        display: none;
      }
    }
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

// Scrollbar
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.notif-items::-webkit-scrollbar {
  width: 4px;
}

.notif-items::-webkit-scrollbar-track {
  background: transparent;
}

.notif-items::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}
</style>